import { ReducerCreators } from '@reduxjs/toolkit';
import { AudiencesState } from '../types';
import LoadAttendees from '@/connection/audiences/all-audiences';
import type { RootState } from '@/store/root-state';
import {
  selectAllAttendeesFields,
  selectAllAttendeesPage,
  selectAllAttendeesPageSize,
  selectAllAttendeesSearch,
  selectAllAttendeesSort,
  selectAllAttendeesSortDirection,
} from '../selectors';
import { selectWorkingProjectUuid } from '@/store/slices/project/selectors';
import { selectActiveChannel } from '@/store/slices/user/selectors';
import type { AttendeeFields, RegistrationFilter, UserStatus } from '@/types/all-attendees.types';
import deepEqual from 'fast-deep-equal';

// status/registrationStatus come in as arguments rather than from the store: their source of
// truth is the URL (the :listType route param and the registrationStatus search param).
export type FetchAllAttendeesArgs = {
  fields?: AttendeeFields;
  status?: UserStatus;
  registrationStatus?: RegistrationFilter;
};

export const fetchAllAttendees = (create: ReducerCreators<AudiencesState>) =>
  create.asyncThunk(
    async (args: FetchAllAttendeesArgs | undefined, { getState, signal }) => {
      const state = getState() as RootState;
      const channel = selectActiveChannel(state);
      const eventUuid = selectWorkingProjectUuid(state)!;
      const page = selectAllAttendeesPage(state);
      const pageSize = selectAllAttendeesPageSize(state);
      const search = selectAllAttendeesSearch(state);
      const sort = selectAllAttendeesSort(state);
      const sortDirection = selectAllAttendeesSortDirection(state);
      const fields = args?.fields ?? selectAllAttendeesFields(state);

      return await LoadAttendees({
        channel,
        eventUuid,
        page: page - 1,
        pageSize,
        search,
        sort,
        sortDirection,
        fields,
        status: args?.status,
        registrationStatusFilter: args?.registrationStatus,
        signal,
      });
    },
    {
      options: {
        condition: (args: FetchAllAttendeesArgs | undefined, { getState }) => {
          const state = getState() as RootState;
          const fields = selectAllAttendeesFields(state);

          if (deepEqual(fields, {})) {
            // fields not yet initialized, skip load
            return false;
          }

          // skip if currently loading and fields have not changed
          if (args?.fields && fields && deepEqual(args.fields, fields)) {
            return !state.audiences.allAttendees.loading;
          }

          return true;
        },
      },
      pending: (state) => {
        state.allAttendees.loading = true;
      },
      fulfilled: (state, action) => {
        state.allAttendees.users = action.payload.data || [];
        state.allAttendees.total = action.payload.total || 0;
        state.allAttendees.loading = false;
      },
      rejected: (state, action) => {
        if (action.meta.aborted) return;
        state.allAttendees.loading = false;
        console.error(action.error);
      },
    }
  );
